import { AlertCircle, Clock3 } from 'lucide-react';
import { Button } from '@/shared/components';
import type { OnboardingDeliveryDiagnosticResponse } from '@/shared/api';
import { formatDateTime } from '@/shared/lib/format';

const ERROR_LABELS: Record<string, string> = {
  messages_app_missing: 'Die Nachrichten-App des Kanalmanagers ist nicht eingerichtet',
  delivery_outcome_unknown: 'Der Anbieter hat die Annahme nicht eindeutig bestätigt',
  legacy_delivery_outcome_unknown: 'Für einen früheren Versand fehlt eine eindeutige Bestätigung',
};

/** Der Zustellweg heisst im Code nach seinem Transport; hier steht, was er für den Gast bedeutet. */
const TRANSPORT_LABELS: Record<OnboardingDeliveryDiagnosticResponse['transport'], string> = {
  Smtp: 'E-Mail',
  Channex: 'OTA-Nachricht',
};

const STATUS_LABELS: Record<OnboardingDeliveryDiagnosticResponse['status'], string> = {
  Staging: 'Sammelt Apartments',
  Pending: 'Wartet auf Versand',
  RetryPending: 'Erneuter Versuch nach Ablehnung geplant',
  Dispatching: 'Versand läuft',
  OutcomeUnknown: 'Versand unklar',
  Delivered: 'Annahme bestätigt',
  DeadLettered: 'Fehlgeschlagen',
  Discarded: 'Verworfen',
};

interface Props {
  delivery: OnboardingDeliveryDiagnosticResponse;
  busy: boolean;
  onRetry: () => void;
  allowRetry?: boolean;
}

export function OnboardingDeliveryDiagnostic({
  delivery,
  busy,
  onRetry,
  allowRetry = true,
}: Props) {
  const error = delivery.errorCode
    ? (ERROR_LABELS[delivery.errorCode] ?? `Versandfehler (Code ${delivery.errorCode})`)
    : null;
  const retryable = delivery.status === 'DeadLettered';

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
      <span className="text-ink-soft">
        {TRANSPORT_LABELS[delivery.transport] ?? delivery.transport} — {delivery.channel} —{' '}
        {delivery.attemptCount} Versuche
      </span>
      <span className={retryable ? 'text-sealwax font-medium' : 'text-ink-soft'}>
        {STATUS_LABELS[delivery.status] ?? delivery.status}
      </span>
      {error && <span className="text-sealwax font-medium">{error}</span>}
      {retryable && allowRetry && (
        <Button size="sm" variant="ghost" loading={busy} onClick={onRetry}>
          Zustellung erneut versuchen
        </Button>
      )}
      {delivery.nextAttemptAt && (
        <span className="text-ink-soft flex items-center gap-1">
          <Clock3 size={12} aria-hidden /> Nächster Versuch:{' '}
          {formatDateTime(delivery.nextAttemptAt)}
        </span>
      )}
      {delivery.deliveredAt && (
        <span className="text-ink-soft">
          Annahme bestätigt: {formatDateTime(delivery.deliveredAt)}
        </span>
      )}
      {retryable && (
        <AlertCircle
          size={13}
          className="text-sealwax"
          strokeWidth={1.6}
          aria-label="Zustellfehler"
        />
      )}
    </div>
  );
}
